import React, { useEffect, useState, useMemo } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import SearchableAssetResults from '../../ui/inputs/SearchableAssetResults';
import { Group, X } from 'lucide-react';
import { useToast } from '../../ui/toast/ToastContext';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import CustomSelect from '../../ui/selects/CustomSelect';

const AddComplementaryAssetModal = ({ onClose, onSuccess, parentAsset }) => {
  const { addToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const lockedAssetType = useMemo(() => {
    if (parentAsset.assetType === 'Equity') {
      return `Equity_${parentAsset.assetKind}`;
    }
    return parentAsset.assetType;
  }, [parentAsset]);

  useEffect(() => {
    if (searchTerm.length < 3 || selectedAsset) {
      setSearchResults([]);
      return;
    }

    const controller = new AbortController();
    const [type, kind] = lockedAssetType.split('_');
    const kindQuery = kind ? `&kind=${kind}` : '';

    fetch(
      `/app/analyst/wallet_configs/wallet_assets/search?q=${encodeURIComponent(searchTerm)}&type=${type}${kindQuery}`,
      { signal: controller.signal }
    )
      .then((res) => res.json())
      .then((data) => setSearchResults(camelizeKeys(data)))
      .catch((err) => console.warn('Busca ignorada:', err));

    return () => controller.abort();
  }, [searchTerm, lockedAssetType, selectedAsset]);

  const handleSelectResult = (asset) => {
    setSelectedAsset(asset);
    setSearchTerm(asset.name);
    setSearchResults([]);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedAsset(null);
    setSearchResults([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedAsset) {
      addToast({ type: 'error', message: 'Por favor, busque e selecione um ativo complementar.' });
      return;
    }

    const [baseType, baseKind] = lockedAssetType.split('_');

    onSuccess({
      assetId: selectedAsset.id,
      assetType: baseType,
      assetKind: baseKind,
      assetName: selectedAsset.name,
      assetCnpj: selectedAsset.cnpj,
      assetCge: selectedAsset.cge,
    });
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Group size={24} className="text-tealMedium" strokeWidth={1.25} />
            Adicionar Ativo Complementar
          </div>
          <button type="button" onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 relative">
          <div className=" border-b border-bluePale py-4 mb-2">
            <p className="font-jost text-lg text-blueDarker">
              <span className="font-semibold">Adicionando complemento ao ativo:</span> {parentAsset.assetName}
            </p>
          </div>

          {/* <CustomSelect
            name="assetType"
            label="Classe do Novo Complemento"
            value={lockedAssetType}
            disabled={true}
            options={[{ value: lockedAssetType, label: lockedAssetType }]}
          /> */}

          <SearchableAssetResults
            name="searchTerm"
            label="Nome do ativo complementar"
            placeholder="Procurar ativo..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedAsset(null);
            }}
            onClear={handleClearSearch}
            autoFocus
            results={searchResults}
            onSelectResult={handleSelectResult}
            renderResult={(asset) => (
              <>
                <span className="font-semibold block">{asset.name}</span>
                <span className="text-xs text-blueSteel">{asset.documentNumber || 'Sem CNPJ'}</span>
              </>
            )}
          />

          <div className="flex justify-end items-center mt-4">
            <div className="flex justify-between gap-2">
              <SecondaryButton type="button" onClick={onClose}>
                CANCELAR
              </SecondaryButton>
              <Button type="submit" disabled={!selectedAsset}>
                ADICIONAR COMPLEMENTO
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddComplementaryAssetModal;
